import type { ReactNode } from "react";

import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export type FlagKind =
  | "low_evidence"
  | "not_scored"
  | "clarify"
  | "duty_unknown"
  | "dropped"
  | "archive"
  | "retired"
  | "not_recorded";

/** Mockup chip tones: `info` = purple, `warn` = danger, `muted` = grey text, `plain` = ink. */
export type ChipTone = "plain" | "muted" | "info" | "warn";

export const TONE_CLASS: Record<ChipTone, string> = {
  plain: "text-ks-ink",
  muted: "text-ks-grey-1",
  info: "border-ks-purple text-ks-purple",
  warn: "border-ks-danger text-ks-danger",
};

export const FLAGS: Record<FlagKind, { label: string; tooltip: string | null; tone: ChipTone }> = {
  low_evidence: { label: "Low evidence", tooltip: "Scored on fewer than 3 criteria. Rank unchanged.", tone: "info" },
  not_scored: { label: "Not scored", tooltip: "No ratings yet. Score it through /source.", tone: "muted" },
  clarify: { label: "Clarify", tooltip: "Answered, no rating yet: the comment says what to check.", tone: "muted" },
  duty_unknown: { label: "Duty unknown", tooltip: "No HS code on this offer.", tone: "warn" },
  dropped: { label: "Dropped", tooltip: null, tone: "muted" },
  archive: { label: "Archive", tooltip: null, tone: "muted" },
  retired: { label: "Retired", tooltip: null, tone: "muted" },
  not_recorded: { label: "Not recorded", tooltip: "No outreach recorded for this option yet.", tone: "muted" },
};

/** A plain labelled chip: counts, statuses and tags that are not one of the fixed flags. */
export function Chip({ tone = "plain", title, children }: { tone?: ChipTone; title?: string; children: ReactNode }) {
  return (
    <Badge variant="outline" title={title} className={TONE_CLASS[tone]}>
      {children}
    </Badge>
  );
}

export function FlagChip({ kind }: { kind: FlagKind }) {
  const flag = FLAGS[kind];
  const chip = (
    <Badge variant="outline" tabIndex={flag.tooltip ? 0 : undefined} className={TONE_CLASS[flag.tone]}>
      {flag.label}
    </Badge>
  );
  if (!flag.tooltip) return chip;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{chip}</TooltipTrigger>
        <TooltipContent>{flag.tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function LinkChip({
  href,
  label,
  missingLabel,
  missingTitle,
}: {
  href: string | null;
  label: string;
  missingLabel: string;
  /** Why the link is missing, shown on hover. */
  missingTitle?: string;
}) {
  if (!href) {
    return (
      <Badge variant="outline" aria-disabled="true" title={missingTitle} className="text-ks-grey-1">
        {missingLabel}
      </Badge>
    );
  }
  return (
    <Badge variant="outline" asChild className={TONE_CLASS.info}>
      <a href={href} target="_blank" rel="noopener noreferrer">
        {label} ↗
      </a>
    </Badge>
  );
}
